import React, { useRef, useEffect, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import axios from 'axios';

/**
 * Quill Editor - FREE Open Source Version
 * 
 * License: BSD-3-Clause (completely free!)
 * - No API key required
 * - No usage limits
 * - No branding or attribution required
 * - Perfect for commercial use
 * 
 * Docs: https://quilljs.com/
 */

// Try to load image resize module
let ImageResize;
try {
  ImageResize = require('quill-image-resize-module-react').default;
  Quill.register('modules/imageResize', ImageResize);
} catch (e) {
  console.warn('Image resize module not available');
}

function QuillEditor({ initialContent, onContentChange, title }) {
  const quillRef = useRef(null);
  const [content, setContent] = useState(initialContent || '');

  useEffect(() => {
    setContent(initialContent || '');
  }, [initialContent]);

  const imageHandler = () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;

      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      quill.insertText(range.index, 'Uploading image...');

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post('/api/content/upload-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        quill.deleteText(range.index, 'Uploading image...'.length);
        quill.insertEmbed(range.index, 'image', response.data.location);
        quill.setSelection(range.index + 1);
      } catch (error) {
        console.error('Image upload failed:', error);
        quill.deleteText(range.index, 'Uploading image...'.length);
        alert('Failed to upload image. Please try again.');
      }
    };
  };

  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'list': 'check' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video', 'formula'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    },
    clipboard: {
      matchVisual: false  // Preserve original HTML structure on paste
    },
    imageResize: ImageResize ? {
      displaySize: true,
      modules: ['Resize', 'DisplaySize']
    } : undefined,
    clipboard: {
      matchVisual: false
    }
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'bullet', 'check', 'indent',
    'direction', 'align',
    'blockquote', 'code-block',
    'link', 'image', 'video', 'formula'
  ];

  const handleContentChange = (value) => {
    setContent(value);
    if (onContentChange) {
      onContentChange(value);
    }
  };

  return (
    <div style={{ background: 'white', borderRadius: '4px' }}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={content}
        onChange={handleContentChange}
        modules={modules}
        formats={formats}
        style={{ height: '500px', marginBottom: '42px' }}
      />
    </div>
  );
}

export default QuillEditor;
